import { useState, useCallback, useMemo, useEffect, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination, Modal } from 'antd';
import dayjs from '@/utils/dayjsConfig';
import { CloseOutlined } from '@ant-design/icons';
import { FilterComponet, ExportFile, BasicTable } from '@/components';
import { FilterParams } from '@/facilities/model/user';
import { parseQueryString, parseMonth } from '@/utils/toolt';
import { useAppSelector } from '@/store';
import { useMenuItem } from '@/hooks';
import { TC, TABLEPAI, RECH_DATE, CHRONIC_LIFTS } from '@/enums/pageEnum';
import useMessageRedux from '@/store/message-redux';
import { getList, getChronicLiftsCnt, getLiftReportDetail, getProperOrg } from '@api';
import { uniqBy } from 'lodash-es';
import {
  PrintButton,
  CollapseFilters,
  ReportTitle,
  WindowPrint,
  PrintContent,
} from '@/pages/report/components';

export default function ChronicLifts() {
  const { t } = useTranslation();
  const orgType = useAppSelector((state) => state.app.organizationType);
  const [isPending, startTransition] = useTransition();
  const [filterDas, setFilterDas] = useState<any>({});
  const [data, setData] = useState<object[]>([]);
  const [printData, setPrintData] = useState<object[]>([]);
  const [printDetails, setPrintDetails] = useState<object[]>([]);
  const [consts, setConsts] = useState({ liftCnt: 0, breakdownCnt: 0 });
  const { message } = useMessageRedux();
  const [pageParams, setPageParams] = useState({ current: 1, size: 20, total: 0 });
  const divClass = 'border-b-[1px] border-[#E8E9ED] dark:border-[#ffffff21] mb-4';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowData, setRowData] = useState<any>();
  const getPathParams = useCallback(() => parseMonth(parseQueryString()), []);
  const isShowPrint = useMemo(() => getPathParams()?.print === 'true', [getPathParams]);
  const isPrintDetail = useMemo(
    () => getPathParams()?.printDetail === 'true',
    [getPathParams],
  );

  const getTableLists = useCallback(
    ({ page, isReset = false }: { page?: number; isReset?: boolean }) => {
      startTransition(async () => {
        const params = getPathParams();
        try {
          const res = await getList({
            url: TABLEPAI.CHRON_LIFT,
            params: {
              page: page || pageParams.current,
              size: pageParams.size,
              ...(isReset ? {} : params),
            },
          });
          const resCnt = await getChronicLiftsCnt({ params });
          setConsts(resCnt.data?.body || {});
          const body = res.data.body;
          setPageParams((prev) => {
            prev.total = body.totalElements;
            prev.current = page || 1;
            return prev;
          });
          if (isShowPrint && body.content.length === pageParams.size) {
            getTableLists({ page: pageParams.current + 1 });
          }
          setPrintData((prev) => uniqBy([...prev, ...body.content], 'id'));
          setData(body.content);
        } catch (error) {
          message.error(JSON.stringify(error));
        }
      });
    },
    [getPathParams, message, pageParams, isShowPrint],
  );

  const fetchProperties = useCallback(async () => {
    const res = await getProperOrg({ type: CHRONIC_LIFTS });
    setFilterDas(res.data?.body?.body || {});
  }, []);

  const fetchPrintDetail = useCallback(async () => {
    startTransition(async () => {
      try {
        const res = await Promise.all(
          printData.map((item: any) => getLiftReportDetail(item.id)),
        );
        const lists: object[] = [];
        printData.forEach((item: any, index: number) => {
          res[index]?.data?.body.forEach((detail: any, detailIndex: number) => {
            const obj = {
              id: `${item.id}-${detail.id}`,
              liftFaultNumber: detail?.liftFaultNumber,
              alarmStartTs: detail?.alarmStartTs,
              faultCode: detail?.faultCode,
              complaintCnt: detail?.complaintCnt,
            };
            lists.push(detailIndex === 0 ? { ...item, ...obj } : obj);
          });
        });
        setPrintDetails(lists);
      } catch (error) {
        message.error(JSON.stringify(error));
      }
    });
  }, [printData, message]);

  useMenuItem({
    callBack: (prev: any) => {
      resetFilters({ isReset: prev?.isReset });
      fetchProperties();
    },
  });

  const filtersFileds: FilterParams[] = useMemo(() => {
    if (orgType === TC) {
      return [
        {
          component: 'cSel',
          field: 'liftCompany',
          label: t('LIFT_COMPANY'),
          dataSource: filterDas?.liftCompany,
          fieldProps: { divClass },
        },
      ];
    }
    return [
      {
        component: 'cSel',
        field: 'townCouncil',
        label: t('TOWN_COUNCIL'),
        dataSource: filterDas?.townCouncil,
        fieldProps: { divClass },
      },
    ];
  }, [t, filterDas, divClass, orgType]);

  const tableTitle = useMemo(() => {
    if (isPrintDetail) return t('movingLiftDetail');
    return `${t('movingLift')} - ${dayjs(new Date()).format('DD MMM YYYY HH:mm')}`;
  }, [t, isPrintDetail]);

  const isLastPage = useMemo(() => {
    return printData.length > 0 && printData.length === pageParams.total;
  }, [printData, pageParams]);

  const isPrintLading = useMemo(() => {
    if (isPrintDetail) return printDetails.length > 0;
    return isLastPage && !isPending;
  }, [isLastPage, isPending, isPrintDetail, printDetails]);

  useEffect(() => {
    if (isLastPage && isPrintDetail) {
      fetchPrintDetail();
    }
  }, [isLastPage, isPrintDetail, fetchPrintDetail]);

  const modelColums = useMemo(
    () => [
      { title: t('liftFaultNumber'), dataIndex: 'liftFaultNumber' },
      {
        title: t('setTime'),
        dataIndex: 'alarmStartTs',
        renderText: (text: number) => (text ? dayjs(text).format(RECH_DATE) : ''),
      },
      { title: t('faultCo'), dataIndex: 'faultCode' },
      { title: t('complaintCount'), dataIndex: 'complaintCnt' },
    ],
    [t],
  );

  const columns = useMemo(() => {
    return [
      { title: t('liftName'), dataIndex: 'liftName' },
      {
        title: orgType === TC ? t('LIFT_COMPANY') : t('TOWN_COUNCIL'),
        dataIndex: orgType === TC ? 'liftCompany' : 'liftTc',
      },
      { title: t('address'), dataIndex: 'buildingAddress' },
      isPrintDetail ? {} : { title: t('complaintCount'), dataIndex: 'complaintCnt' },
      { title: t('noOfBreak'), dataIndex: 'breakdownCnt' },
      ...(isPrintDetail ? modelColums : []),
    ];
  }, [t, orgType, isPrintDetail, modelColums]);

  const getPrintContent = (d: object[]) => {
    return (
      <PrintContent
        columns={columns}
        loading={isShowPrint ? false : isPending}
        tableTitle={tableTitle}
        dataSource={d}
        tableChildren={
          <div className="my-3">
            <span>Total lifts: {consts.liftCnt}</span>
            <span className="float-right">
              Total No. of breakdowns: {consts.breakdownCnt}
            </span>
          </div>
        }
        className="border-[#F1F0F5] dark:border-[#ffffff21] border-[1px] caseTable"
        onCilickRow={(record: any) => {
          setIsModalOpen(true);
          setRowData(record);
        }}
      />
    );
  };

  const resetFilters = useCallback(
    ({ page, isReset }: { page?: number; isReset?: boolean }) => {
      setPageParams((prev) => {
        prev.current = page || 1;
        return prev;
      });
      getTableLists({ page, isReset });
    },
    [getTableLists],
  );

  const getModalPureData = useMemo(() => {
    return (
      <Modal
        open={isModalOpen}
        width={800}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        destroyOnClose
        closeIcon={
          <CloseOutlined
            className="text-[#000000d9] dark:text-[#ffffff73]"
            style={{ fontSize: 24 }}
          />
        }
      >
        <h4 className="text-[20px] font-medium mt-7 text-center">
          {t('movingLiftDetail')}
        </h4>
        <div className="flex gap-6 my-5 font-medium">
          <div>
            <span>{t('liftName')}: </span>
            <span className="text-[#AB3E3E]">{rowData?.liftName}</span>
          </div>
          <div>
            <span>{t('liftAge')}: </span>
            <span className="text-[#AB3E3E]">{rowData?.liftAgeFormat}</span>
          </div>
          <div>
            <span>{orgType === TC ? t('LIFT_COMPANY') : t('TOWN_COUNCIL')}: </span>
            <span className="text-[#AB3E3E]">
              {orgType === TC ? rowData?.liftCompany : rowData?.liftTc}
            </span>
          </div>
          <div>
            <span>{t('address')}: </span>
            <span className="text-[#AB3E3E]">{rowData?.buildingAddress}</span>
          </div>
        </div>
        <BasicTable
          isShowCheckbox={false}
          columns={modelColums}
          isDefaultToolBar={false}
          rowKey="id"
          size={10}
          className="!p-0 min-h-[calc(250px)]"
          dataKey="data.body"
          requestUrl={`/api/reports/chronic-lifts/${rowData?.id}/detail`}
        />
      </Modal>
    );
  }, [isModalOpen, t, rowData, orgType, modelColums]);

  const getExportFile = useCallback(
    (url: string, text: string) => {
      return (
        <ExportFile
          disabled={!data.length}
          buttonText={text}
          className="mr-3"
          onClick={() => {
            const { origin } = new URL(window.location.href);
            const downurl = new URLSearchParams({ ...getPathParams() });
            window.open(
              `${origin}/api/reports/chronic-lifts/${url}-export?${downurl?.toString()}`,
            );
          }}
        />
      );
    },
    [data, getPathParams],
  );

  if (isShowPrint) {
    return (
      <WindowPrint isPrintLading={isPrintLading}>
        {getPrintContent(isPrintDetail ? printDetails : printData)}
      </WindowPrint>
    );
  }

  return (
    <div className="p-4">
      <ReportTitle title={t('chronicLiftre')} />
      <CollapseFilters>
        <FilterComponet
          className="mb-[2px] filtersFromClass !py-0 !pl-0"
          layout="horizontal"
          submitNode={<>{t('preview')}</>}
          fieldsProp={filtersFileds}
          submit={() => {
            resetFilters({});
          }}
          reset={() => {
            resetFilters({});
          }}
        />
      </CollapseFilters>
      <div className="mt-4 p-4 bg-[#fff] dark:bg-[#141414]">
        <div className="flex justify-start items-center pb-4 border-b-[1px] border-[#E8E9ED] dark:border-[#ffffff21]">
          {getExportFile('summary', t('exSum'))}
          <PrintButton
            disabled={!data.length}
            buttonName={t('printSum')}
            className="mr-6"
            otherParams={getPathParams()}
          />
          {getExportFile('detail', `${t('export')} ${t('detail')}`)}
          <PrintButton
            disabled={!data.length}
            buttonName={`${t('print')} ${t('detail')}`}
            otherParams={{ ...getPathParams(), printDetail: 'true' }}
          />
        </div>
        {getPrintContent(data)}
        {pageParams.total > pageParams.size && (
          <div className="pt-4">
            <Pagination
              align="end"
              onChange={(page) => {
                resetFilters({ page });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              current={pageParams.current}
              pageSize={pageParams.size}
              total={pageParams.total}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
      {getModalPureData}
    </div>
  );
}
